import { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { Download, ExternalLink, ImageIcon } from 'lucide-react'
import type { ToolRendererProps } from '@/config/types'
import { AgentToolCallStatus } from '@/runtime/types'
import { useAgentRuntime } from '@/context/AgentProvider'
import { useTranslations } from '@/hooks/useTranslations'
import { coerceImageResult, type GeneratedImageRef } from './helpers'

export const ImageToolRenderer = ({ call }: ToolRendererProps) => {
  const runtime = useAgentRuntime()
  const t = useTranslations()
  const result = useMemo(() => coerceImageResult(call), [call])

  // Preferred source: a stable backend URL built from the s3Key. The backend
  // authenticates and redirects to a freshly presigned S3 URL per request, so
  // the reference never expires — unlike the presigned url stored in the tool
  // output, which is only kept as a fallback.
  const stableUrl = useCallback(
    (image: GeneratedImageRef): string | undefined => {
      const transport = runtime.config.transport
      if (transport.mode !== 'proxy' || !image.s3Key?.startsWith('generated-images/')) {
        return undefined
      }
      const path = image.s3Key.split('/').map(encodeURIComponent).join('/')
      return `${transport.baseUrl}/files/${path}`
    },
    [runtime.config.transport]
  )

  // Fallback for backends without the stable-URL redirect route: re-presign
  // through the POST endpoint when an image fails to load. Bounded per image so
  // a genuinely missing object cannot cause an error loop.
  const MAX_REFRESH_ATTEMPTS = 3
  const [resolvedUrls, setResolvedUrls] = useState<Record<string, string>>({})
  const refreshAttempts = useRef<Map<string, number>>(new Map())

  const resolveUrl = useCallback(
    async (image: GeneratedImageRef) => {
      const attempts = refreshAttempts.current.get(image.attachmentId) ?? 0
      if (!image.s3Key || attempts >= MAX_REFRESH_ATTEMPTS) return
      refreshAttempts.current.set(image.attachmentId, attempts + 1)

      const transport = runtime.config.transport
      if (transport.mode !== 'proxy') return

      try {
        const headers: Record<string, string> = { 'Content-Type': 'application/json' }
        if (transport.headers) {
          Object.assign(headers, transport.headers)
        } else if (transport.apiKey) {
          headers['Authorization'] = `Bearer ${transport.apiKey}`
        }
        const response = await fetch(`${transport.baseUrl}/files/download/presigned`, {
          method: 'POST',
          credentials: 'include',
          headers,
          body: JSON.stringify({ s3Key: image.s3Key })
        })
        if (!response.ok) return
        const data = await response.json()
        if (typeof data?.url === 'string') {
          setResolvedUrls((prev) => ({ ...prev, [image.attachmentId]: data.url }))
        }
      } catch {
        // Keep whatever URL we had; the broken-image state stays visible.
      }
    },
    [runtime.config.transport]
  )

  useEffect(() => {
    result.images
      .filter((image) => !image.url && !stableUrl(image))
      .forEach((image) => void resolveUrl(image))
  }, [result.images, resolveUrl, stableUrl])

  const downloadImage = useCallback(async (image: GeneratedImageRef, src: string) => {
    try {
      const response = await fetch(src, { credentials: 'include' })
      const blob = await response.blob()
      const objectUrl = URL.createObjectURL(blob)
      const anchor = document.createElement('a')
      anchor.href = objectUrl
      anchor.download = image.name
      anchor.click()
      URL.revokeObjectURL(objectUrl)
    } catch {
      // Cross-origin fetch can be blocked by bucket CORS; opening the image still works.
      window.open(src, '_blank', 'noreferrer')
    }
  }, [])

  if (call.status === AgentToolCallStatus.Failed || result.error) {
    return (
      <div className='mp-agent-tool-body'>
        <div className='mp-agent-tool-simple-message'>{t.imageGenerationFailed}</div>
        {result.error ? <div className='mp-agent-tool-error'>{result.error}</div> : null}
      </div>
    )
  }

  if (call.status !== AgentToolCallStatus.Succeeded) {
    return (
      <div className='mp-agent-tool-body mp-agent-image-tool'>
        <div className='mp-agent-image-tool-loading'>
          <ImageIcon size={16} />
          <span>{t.imageGenerationGenerating}</span>
        </div>
      </div>
    )
  }

  if (!result.images.length) {
    return <div className='mp-agent-tool-simple-message'>{t.imageGenerationNone}</div>
  }

  return (
    <div className='mp-agent-tool-body mp-agent-image-tool'>
      <div className='mp-agent-image-tool-grid'>
        {result.images.map((image) => {
          const src = resolvedUrls[image.attachmentId] ?? stableUrl(image) ?? image.url
          return (
            <figure key={image.attachmentId} className='mp-agent-image-tool-item'>
              {src ? (
                <img
                  src={src}
                  alt={result.prompt || image.name}
                  title={result.prompt}
                  loading='lazy'
                  onError={() => void resolveUrl(image)}
                />
              ) : (
                <div className='mp-agent-image-tool-placeholder'>
                  <ImageIcon size={20} />
                </div>
              )}
              {src ? (
                <figcaption className='mp-agent-image-tool-actions'>
                  <a href={src} target='_blank' rel='noreferrer' title={t.imageGenerationOpen}>
                    <ExternalLink size={14} />
                  </a>
                  <button type='button' onClick={() => void downloadImage(image, src)} title={t.imageGenerationDownload}>
                    <Download size={14} />
                  </button>
                </figcaption>
              ) : null}
            </figure>
          )
        })}
      </div>
    </div>
  )
}
